import {
  useDataProvider,
  useGetIdentity,
  useNotification,
} from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";

import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { Sentry } from "@/lib/sentry";
import type { IIdentity } from "@/providers/auth-provider";
import type {
  SiteHealthFindingsPage,
  SiteHealthRunResponse,
  SiteHealthStatus,
} from "@/types/tina";

/** Cache key root for every site-health query on this page. */
export const SITE_HEALTH_QUERY_ROOT = "tina-site-health";

export type SnoozeDays = 3 | 7 | 30;

/**
 * The reads and writes `/tina/site-health` needs, through the data provider's `custom`.
 *
 * The actor is the curator's Firebase uid, as on the venue audit: tina authenticates the
 * SERVICE, so the body is the only place a person can come from, and an unloaded identity
 * blocks the action rather than inventing one.
 */
export const useSiteHealthRepository = () => {
  const { custom } = useDataProvider()("tina");
  const { open } = useNotification();
  const queryClient = useQueryClient();
  const { data: identity } = useGetIdentity<IIdentity>();
  const actorId = identity?.id;

  function refresh(): void {
    void queryClient.invalidateQueries({ queryKey: [SITE_HEALTH_QUERY_ROOT] });
  }

  function notLoaded(action: string): void {
    open?.({
      type: "error",
      message: `Cannot ${action} yet`,
      description:
        "Your account has not finished loading, and this action has to be attributable. Reload the page and try again.",
    });
  }

  /**
   * Run one write, breadcrumbed, and swallow a failure into `null` rather than
   * rethrowing: the venue audit's own dismiss can afford to throw because its list is a
   * `useTable` that already renders an error state, but this page's dialogs are the only
   * thing standing between a click and an unhandled rejection, and they need a plain
   * success/failure signal to decide whether to close.
   */
  async function guarded<T>(
    category: string,
    message: string,
    failure: string,
    run: () => Promise<T>,
  ): Promise<T | null> {
    Sentry.addBreadcrumb({
      category: `tina-site-health.${category}`,
      message,
      level: "info",
    });
    try {
      return await run();
    } catch (error) {
      Sentry.captureException(error);
      open?.({
        type: "error",
        message: failure,
        description: toHumanErrorMessage(error),
      });
      return null;
    }
  }

  async function listFindings(
    status: SiteHealthStatus,
  ): Promise<SiteHealthFindingsPage> {
    const response = await custom!({
      url: "",
      method: "get",
      query: { status, limit: 500, offset: 0 },
      meta: { urlPath: "site-health/findings" },
    });
    return response.data as SiteHealthFindingsPage;
  }

  /**
   * `false` on either a blocked (identity not loaded) or a failed request — never throws.
   * The Snooze dialog reads this to decide whether it may close, and "Snooze all" reads it
   * to decide whether to stop the batch.
   */
  async function snooze(
    findingId: string,
    days: SnoozeDays,
    note: string | null,
  ): Promise<boolean> {
    if (!actorId) {
      notLoaded("snooze");
      return false;
    }
    const result = await guarded(
      "snooze",
      "Snoozing a site-health finding",
      "Failed to snooze",
      () =>
        custom!({
          url: "",
          method: "post",
          payload: { days, note: note || null, snoozed_by: actorId },
          meta: {
            urlPath: `site-health/findings/${encodeURIComponent(findingId)}/snooze`,
          },
        }),
    );
    if (result === null) return false;
    refresh();
    open?.({
      type: "success",
      message: `Snoozed for ${days} days`,
      description: "It comes back after that, or sooner if it turns red.",
    });
    return true;
  }

  /**
   * Closes one finding at once, open or snoozed. Tina waits for fresh data before it
   * checks the site again; if it is still broken then, a new finding appears — this page
   * does nothing further about that. Same `false`-never-throws contract as {@link snooze}.
   */
  async function solve(findingId: string): Promise<boolean> {
    if (!actorId) {
      notLoaded("mark solved");
      return false;
    }
    // A 409 means tina already closed it (the noon run, or another curator): the card is
    // where the curator wants it, so it counts as done and "Solve all" carries on.
    let alreadyClosed = false;
    const result = await guarded(
      "solve",
      "Marking a site-health finding solved",
      "Failed to mark solved",
      async () => {
        try {
          return await custom!({
            url: "",
            method: "post",
            payload: { solved_by: actorId },
            meta: {
              urlPath: `site-health/findings/${encodeURIComponent(findingId)}/solve`,
            },
          });
        } catch (error) {
          if ((error as { statusCode?: number }).statusCode !== 409) throw error;
          alreadyClosed = true;
          return null;
        }
      },
    );
    // Refresh on every outcome: the page never refetches by itself, so a card that failed
    // or was already closed would otherwise stay on screen until a reload.
    refresh();
    if (alreadyClosed) {
      open?.({
        type: "success",
        message: "Already closed",
        description: "Tina had closed this card already.",
      });
      return true;
    }
    if (result === null) return false;
    open?.({
      type: "success",
      message: "Marked solved",
      description: "It comes back only if the site is still broken once Tina has fresh data.",
    });
    return true;
  }

  /** Same `false`-never-throws contract as {@link snooze}. */
  async function disable(site: string, reason: string): Promise<boolean> {
    if (!actorId) {
      notLoaded("disable a site");
      return false;
    }
    // The site travels in the BODY: names like `allevents/ro/cluj` contain `/`.
    const result = await guarded(
      "disable",
      "Disabling a site from Site health",
      "Failed to disable the site",
      () =>
        custom!({
          url: "",
          method: "post",
          payload: { site, reason, disabled_by: actorId },
          meta: { urlPath: "site-health/disable" },
        }),
    );
    if (result === null) return false;
    refresh();
    open?.({
      type: "success",
      message: "Site disabled",
      description: `${site} will not run again until it is re-enabled.`,
    });
    return true;
  }

  /** `null` on a failed request — never throws. See {@link guarded}. */
  async function runNow(): Promise<SiteHealthRunResponse | null> {
    const response = await guarded(
      "run",
      "Starting the site-health job",
      "Failed to start the check",
      () =>
        custom!({
          url: "",
          method: "post",
          payload: {},
          meta: { urlPath: "site-health/run" },
        }),
    );
    if (response === null) return null;
    const outcome = response.data as SiteHealthRunResponse;
    refresh();
    if (outcome.kind === "dispatched") {
      open?.({
        type: "success",
        message: "Site health started",
        description: "It takes about a minute. Refresh this page when it is done.",
      });
    } else if (outcome.kind === "noop") {
      open?.({
        type: "error",
        message: "Not started",
        description: "The site-health job is not configured on this environment.",
      });
    } else {
      open?.({
        type: "error",
        message: "Failed to start the check",
        description:
          outcome.error_message ?? "The job could not be started and gave no reason.",
      });
    }
    return outcome;
  }

  return { listFindings, snooze, solve, disable, runNow, actorId };
};
